<template>
  <div>
    <BaseModal class="add-linked-customer-modal" v-model="isOpenModal">
      <template v-slot:header>
        Link Customer to Infoauto
      </template>

      <div class="body p-6 pt-0">
        <!-- Mobile tabs (hidden on desktop) -->
        <div class="mobile-tabs">
          <button
            :class="['mobile-tab', { active: mobileTab === 'search' }]"
            @click="mobileTab = 'search'"
            type="button"
          >
            Find Existing
          </button>
          <button
            :class="['mobile-tab', { active: mobileTab === 'create' }]"
            @click="mobileTab = 'create'"
            type="button"
          >
            Create New
          </button>
        </div>

        <div class="two-col-layout">
          <!-- LEFT: Search Section -->
          <div
            :class="['search-col', { 'mobile-hidden': mobileTab !== 'search' }]"
          >
            <h2 class="desktop-heading">Find Existing Customer</h2>
            <div class="input-item">
              <label>Search</label>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Email, name or surname..."
                class="regular bordered full"
                @input="onSearchInput"
              />
            </div>

            <!-- Loading -->
            <div v-if="searchLoading" class="message-box mt-2">
              Searching...
            </div>

            <!-- No results -->
            <div
              v-else-if="searched && searchResults.length === 0"
              class="message-box mt-2"
            >
              No customers found.
            </div>

            <!-- Results -->
            <div v-else-if="searchResults.length > 0" class="mt-2">
              <p class="text-gray-500 text-sm mb-1">
                {{ searchResults.length }} customer(s) found
              </p>
              <div class="search-results-list">
                <div
                  v-for="customer in searchResults"
                  :key="customer.id"
                  class="search-result-row"
                >
                  <div class="search-result-info">
                    <span class="search-result-name"
                      >{{ customer.name }} {{ customer.surname }}</span
                    >
                    <span class="search-result-email">{{
                      customer.email
                    }}</span>
                    <span
                      v-if="customer.infoautoId"
                      class="search-result-linked-badge"
                    >
                      Linked: {{ customer.infoautoId }}
                    </span>
                  </div>
                  <button
                    @click="handleLinkClick(customer)"
                    :disabled="linkingCustomerId === customer.id"
                    class="btn-success-button"
                  >
                    {{ linkingCustomerId === customer.id ? "..." : "Link" }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Vertical divider (desktop only) -->
          <div class="col-divider">
            <span>OR</span>
          </div>

          <!-- RIGHT: Create Section -->
          <div
            :class="['create-col', { 'mobile-hidden': mobileTab !== 'create' }]"
          >
            <h2 class="desktop-heading">Create New Customer</h2>
            <CreateWebCustomerForm
              :infoautoId="infoautoId"
              submitLabel="Create & Link"
              @customerCreated="onCustomerCreated"
            />
          </div>
        </div>
      </div>
    </BaseModal>

    <BaseModal
      class="confirm-modal"
      v-if="pendingLinkCustomer"
      v-model="showConfirmModal"
      @input="
        val => {
          if (!val) cancelLink();
        }
      "
    >
      <template v-slot:header>Confirm Link</template>
      <div class="p-6">
        <div v-if="pendingLinkInfoautoLoading" class="message-box">
          Loading current linked customer...
        </div>
        <template v-else>
          <p style="margin-bottom: 16px; font-size: 14px; color: #374151;">
            <strong
              >{{ pendingLinkCustomer.name }}
              {{ pendingLinkCustomer.surname }}</strong
            >
            is already linked to infoauto customer
            <strong>{{
              pendingLinkInfoautoName || pendingLinkCustomer.infoautoId
            }}</strong
            >. This will be replaced with <strong>{{ infoautoId }}</strong
            >. Continue?
          </p>
          <div style="display: flex; gap: 8px; justify-content: flex-end;">
            <button @click="cancelLink" class="btn-cancel-button">
              Cancel
            </button>
            <button
              @click="confirmLink"
              class="btn-success-button"
              :disabled="linkingCustomerId === pendingLinkCustomer.id"
            >
              {{
                linkingCustomerId === pendingLinkCustomer.id ? "..." : "Confirm"
              }}
            </button>
          </div>
        </template>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import {
  searchWebCustomers,
  linkCustomerToInfoauto
} from "@/api/store/CustomersService.js";
import { infoautoGetSingleCustomer } from "@/api/store/Infoauto.js";
import CreateWebCustomerForm from "@/components/customer/CreateWebCustomerForm.vue";

export default {
  components: { CreateWebCustomerForm },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    infoautoId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isOpenModal: this.value,
      mobileTab: "search",
      searchQuery: "",
      searchResults: [],
      searched: false,
      searchLoading: false,
      linkingCustomerId: null,
      pendingLinkCustomer: null,
      showConfirmModal: false,
      pendingLinkInfoautoName: null,
      pendingLinkInfoautoLoading: false
    };
  },
  watch: {
    value(val) {
      this.isOpenModal = val;
    },
    isOpenModal(val) {
      this.$emit("input", val);
    }
  },
  methods: {
    onSearchInput() {
      clearTimeout(this._searchTimer);
      this.pendingLinkCustomer = null;
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        this.searched = false;
        return;
      }
      this._searchTimer = setTimeout(() => this.searchCustomers(), 350);
    },

    async searchCustomers() {
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        this.searched = false;
        return;
      }

      this.searchLoading = true;
      this.searched = true;
      try {
        const response = await searchWebCustomers(this.searchQuery);
        // Handle response structure: could be array, {data: []}, or {customers: []}
        if (Array.isArray(response)) {
          this.searchResults = response;
        } else if (response.customers) {
          this.searchResults = Array.isArray(response.customers)
            ? response.customers
            : [];
        } else if (response.data) {
          this.searchResults = Array.isArray(response.data)
            ? response.data
            : [];
        } else {
          this.searchResults = [];
        }
      } catch (err) {
        this.$notify({
          type: "error",
          title: "Search Error",
          message: "Failed to search customers"
        });
        console.error(err);
      } finally {
        this.searchLoading = false;
      }
    },

    async linkExistingCustomer(customer) {
      this.linkingCustomerId = customer.id;
      try {
        await linkCustomerToInfoauto(customer.id, this.infoautoId);
        this.$emit("customerLinked", customer);
        this.pendingLinkCustomer = null;
        this.showConfirmModal = false;
        this.isOpenModal = false;
      } catch (err) {
        this.$toast.open({
          message: "Failed to link customer",
          type: "error",
          position: "top-right"
        });
        console.error(err);
      } finally {
        this.linkingCustomerId = null;
      }
    },

    async handleLinkClick(customer) {
      if (customer.infoautoId) {
        this.pendingLinkCustomer = customer;
        this.pendingLinkInfoautoName = null;
        this.pendingLinkInfoautoLoading = true;
        this.showConfirmModal = true;
        try {
          const infoautoCustomer = await infoautoGetSingleCustomer(
            customer.infoautoId
          );
          const info = infoautoCustomer.generalInfo || infoautoCustomer;
          this.pendingLinkInfoautoName =
            [info.name, info.surname].filter(Boolean).join(" ") ||
            customer.infoautoId;
        } catch {
          this.pendingLinkInfoautoName = customer.infoautoId;
        } finally {
          this.pendingLinkInfoautoLoading = false;
        }
      } else {
        this.linkExistingCustomer(customer);
      }
    },

    confirmLink() {
      this.linkExistingCustomer(this.pendingLinkCustomer);
    },

    cancelLink() {
      this.pendingLinkCustomer = null;
      this.pendingLinkInfoautoName = null;
      this.showConfirmModal = false;
    },

    onCustomerCreated(customer) {
      this.$emit("customerLinked", customer);
      this.isOpenModal = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";
.add-linked-customer-modal {
  ::v-deep .modal-content {
    max-height: 90vh;
    overflow-y: auto;
  }
}

.search-result-linked-badge {
  font-size: 11px;
  color: #b45309;
  background-color: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 1px 6px;
  width: fit-content;
}

.confirm-overwrite-banner {
  margin-top: 10px;
  padding: 12px;
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
}

.confirm-overwrite-text {
  font-size: 13px;
  color: #78350f;
  margin-bottom: 10px;
}

.confirm-overwrite-actions {
  display: flex;
  gap: 8px;
}

.btn-cancel-button {
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 5px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;

  &:hover {
    background-color: #f3f4f6;
  }
}

.search-results-list {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow-y: auto;
  max-height: 350px;
}

.search-result-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9fafb;
  }
}

.search-result-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  min-width: 0;
}

.search-result-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-email {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
// Mobile tabs
.mobile-tabs {
  display: none;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    display: flex;
  }
}

.mobile-tab {
  flex: 1;
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &.active {
    color: #16a34a;
    border-bottom-color: #16a34a;
  }
}

// Hide non-active panel on mobile
@media (max-width: 640px) {
  .mobile-hidden {
    display: none !important;
  }
  .col-divider {
    display: none !important;
  }
  .two-col-layout {
    grid-template-columns: 1fr !important;
  }
  .search-col {
    padding-left: 0 !important;
  }
  .create-col {
    padding-right: 0 !important;
  }
}

.desktop-heading {
  @media (max-width: 640px) {
    display: none;
  }
}

.two-col-layout {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: start;
  min-height: 360px;
}

.search-col,
.create-col {
  padding: 0 16px;
}

.search-col {
  padding-left: 0;
}

.create-col {
  padding-right: 0;
}

.col-divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 8px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    background-color: #e2e8f0;
    transform: translateX(-50%);
  }

  span {
    background: #fff;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    letter-spacing: 0.05em;
    position: relative;
    z-index: 1;
  }
}
</style>
